import {PencilIcon, TrashIcon} from '@heroicons/react/24/outline'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {
  SPONSOR_MANAGER_ROUTE,
  TOOL_SELECTOR_ROUTE
} from '../../global-constants/routingConstants'
import useCreateSponsor from '../../hooks/useCreateSponsor'
import useDeleteSponsor from '../../hooks/useDeleteSponsor'
import useDeleteSponsorImage from '../../hooks/useDeleteSponsorImage'
import useSponsors, {Sponsor, SponsorType} from '../../hooks/useSponsors'
import useUpdateSponsorImage from '../../hooks/useUpdateSponsorImage'
import useUpdateSponsorName from '../../hooks/useUpdateSponsorName'
import AssignCommunitySponsorDialog from '../AssignCommunitySponsorDialog'
import AssignNationalSponsorDialog from '../AssignNationalSponsorDialog'
import Breadcrumb from '../Breadcrumb'
import BreadcrumbItem from '../Breadcrumb/BreadcrumbItem'
import Divider from '../Divider'
import FlatButton from '../FlatButton'
import Layout from '../Layout'
import LoadingIndicator from '../LoadingIndicator'
import TitleText from '../TitleText'
import Toast from '../Toast'
import AddSponsorDialog from './AddSponsorDialog'
import DeleteSponsorDialog from './DeleteSponsorDialog'
import EditSponsorDialog from './EditSponsorDialog'
import ImageUpload from './components/ImageUpload'

function SponsorCard({
  sponsor,
  onEdit,
  onDelete,
  onAssign,
  onImageUpload,
  onImageDelete
}: {
  sponsor: Sponsor
  onEdit: (sponsor: Sponsor) => void
  onDelete: (sponsor: Sponsor) => void
  onAssign?: (sponsor: Sponsor) => void
  onImageUpload: (sponsor: Sponsor, image: File) => void
  onImageDelete: (sponsor: Sponsor) => void
}) {
  return (
    <li aria-label={sponsor.name}>
      <div className="flex items-center justify-between gap-x-3 rounded px-2 py-4">
        <span className="flex items-center gap-x-3">
          <ImageUpload
            uploadKey={sponsor.id}
            uri={sponsor.imageUrl || null}
            onUpload={(_, image) => onImageUpload(sponsor, image)}
            onDelete={() => onImageDelete(sponsor)}
            size="list"
          />
          <span className="typography-headline-5 text-navy">
            {sponsor.name}
          </span>
        </span>
        <span className="flex items-center space-x-2">
          {onAssign && (
            <FlatButton
              variant="success"
              onClick={() => onAssign(sponsor)}
              aria-label={`Assign ${sponsor.name}`}
            >
              <FormattedMessage id="admin.manageSponsors.assign" />
            </FlatButton>
          )}
          <FlatButton
            variant="secondary"
            icon={<PencilIcon className="size-4" />}
            onClick={() => onEdit(sponsor)}
            aria-label={`Edit ${sponsor.name}`}
          ></FlatButton>
          <FlatButton
            variant="danger"
            icon={<TrashIcon className="size-4" />}
            onClick={() => onDelete(sponsor)}
            aria-label={`Delete ${sponsor.name}`}
          ></FlatButton>
        </span>
      </div>
    </li>
  )
}

function SponsorSection({
  title,
  testId,
  sponsors,
  onAddSponsor,
  onEditSponsor,
  onDeleteSponsor,
  onAssignSponsor,
  onImageUpload,
  onImageDelete
}: {
  title: React.ReactNode
  testId: string
  sponsors: ReadonlyArray<Sponsor>
  onAddSponsor: () => void
  onEditSponsor: (sponsor: Sponsor) => void
  onDeleteSponsor: (sponsor: Sponsor) => void
  onAssignSponsor?: (sponsor: Sponsor) => void
  onImageUpload: (sponsor: Sponsor, image: File) => void
  onImageDelete: (sponsor: Sponsor) => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="typography-headline-4 text-navy">{title}</h2>
        <FlatButton
          variant="success"
          onClick={onAddSponsor}
          data-testid={`add-${testId}-sponsor-button`}
        >
          <FormattedMessage id="admin.manageSponsors.add_sponsor" />
        </FlatButton>
      </div>

      {sponsors.length > 0 ? (
        <ul
          role="list"
          className="flex w-full flex-1 flex-col rounded bg-white"
        >
          {sponsors.map((sponsor, index) => (
            <React.Fragment key={sponsor.id}>
              <SponsorCard
                sponsor={sponsor}
                onEdit={onEditSponsor}
                onDelete={onDeleteSponsor}
                onAssign={onAssignSponsor}
                onImageUpload={onImageUpload}
                onImageDelete={onImageDelete}
              />
              {index < sponsors.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </ul>
      ) : (
        <div className="rounded bg-white p-8 text-center">
          <p className="typography-body-2 text-grey-tertiary-30">
            <FormattedMessage id="admin.manageSponsors.no_sponsors" />
          </p>
        </div>
      )}
    </div>
  )
}

function SponsorManagerPage() {
  const [state, setState] = useState<{
    selectedSponsor: Sponsor | null
    editingSponsorId: string | null
    assigningSponsorId: string | null
    fetchError: Error | null
    operationError: Error | null
    directOperationError: Error | null
    addingSponsorType: SponsorType
    showEditDialog: boolean
    showDeleteDialog: boolean
    showAddDialog: boolean
    showAssignDialog: boolean
    assignDialogType: 'NATIONAL' | 'COMMUNITY' | null
  }>({
    selectedSponsor: null,
    editingSponsorId: null,
    assigningSponsorId: null,
    fetchError: null,
    operationError: null,
    directOperationError: null,
    addingSponsorType: 'NATIONAL',
    showEditDialog: false,
    showDeleteDialog: false,
    showAddDialog: false,
    showAssignDialog: false,
    assignDialogType: null
  })

  const handleSponsorsError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      fetchError: error
    }))
  }, [])

  const handleOperationError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      operationError: error
    }))
  }, [])

  const handleDirectOperationError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      directOperationError: error
    }))
  }, [])

  const {
    nationalSponsors,
    communitySponsors,
    isLoading: getSponsorsBusy,
    sponsors,
    refetch
  } = useSponsors({
    onError: handleSponsorsError
  })

  const currentEditingSponsor = useMemo(() => {
    if (!state.editingSponsorId) return null
    return (
      [...nationalSponsors, ...communitySponsors].find(
        sponsor => sponsor.id === state.editingSponsorId
      ) || null
    )
  }, [state.editingSponsorId, nationalSponsors, communitySponsors])

  const currentAssigningSponsor = useMemo(() => {
    if (!state.assigningSponsorId) return null
    return (
      [...nationalSponsors, ...communitySponsors].find(
        sponsor => sponsor.id === state.assigningSponsorId
      ) || null
    )
  }, [state.assigningSponsorId, nationalSponsors, communitySponsors])

  const handleCreateSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      showAddDialog: false
    }))
    refetch()
  }, [refetch])

  const handleDeleteSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      showDeleteDialog: false,
      selectedSponsor: null
    }))
    refetch()
  }, [refetch])

  const {createSponsor, busy: creatingBusy} = useCreateSponsor({
    onSuccess: handleCreateSuccess,
    onError: handleOperationError
  })

  const {deleteSponsor, busy: deletingBusy} = useDeleteSponsor({
    onSuccess: handleDeleteSuccess,
    onError: handleOperationError
  })

  const handleAddNationalSponsor = () => {
    setState(prev => ({
      ...prev,
      addingSponsorType: 'NATIONAL',
      showAddDialog: true,
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  const handleAddCommunitySponsor = () => {
    setState(prev => ({
      ...prev,
      showAddDialog: true,
      addingSponsorType: 'COMMUNITY',
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  const handleAddConfirm = (name: string, type: SponsorType, image?: File) => {
    createSponsor(name, type, image)
  }

  const handleAddCancel = () => {
    setState(prev => ({
      ...prev,
      showAddDialog: false,
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  const handleEditSponsor = (sponsor: Sponsor) => {
    setState(prev => ({
      ...prev,
      showEditDialog: true,
      editingSponsorId: sponsor.id,
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  // Store promise resolvers for coordinating async operations on edit
  const editOperationResolvers = useRef<{
    imageUpdate?: {resolve: () => void; reject: (error: Error) => void}
    nameUpdate?: {resolve: () => void; reject: (error: Error) => void}
    imageDelete?: {resolve: () => void; reject: (error: Error) => void}
  }>({})

  // Separate resolvers for direct image operations (from list view)
  const directImageOperationResolver = useRef<{
    resolve: () => void
    reject: (error: Error) => void
  } | null>(null)

  const {deleteSponsorImage, busy: deletingSponsorImageBusy} =
    useDeleteSponsorImage({
      onSuccess: () => {
        editOperationResolvers.current.imageDelete?.resolve()
        directImageOperationResolver.current?.resolve()
      },
      onError: error => {
        // Use appropriate error handler based on operation context
        if (directImageOperationResolver.current) {
          handleDirectOperationError(error)
        } else {
          handleOperationError(error)
        }
        editOperationResolvers.current.imageDelete?.reject(error)
        directImageOperationResolver.current?.reject(error)
      }
    })

  const {updateSponsorImage, busy: imageUpdateBusy} = useUpdateSponsorImage({
    onSuccess: () => {
      editOperationResolvers.current.imageUpdate?.resolve()
      directImageOperationResolver.current?.resolve()
    },
    onError: error => {
      // Use appropriate error handler based on operation context
      if (directImageOperationResolver.current) {
        handleDirectOperationError(error)
      } else {
        handleOperationError(error)
      }
      editOperationResolvers.current.imageUpdate?.reject(error)
      directImageOperationResolver.current?.reject(error)
    }
  })

  const {updateSponsorName, busy: nameUpdateBusy} = useUpdateSponsorName({
    onSuccess: () => {
      editOperationResolvers.current.nameUpdate?.resolve()
    },
    onError: error => {
      handleOperationError(error)
      editOperationResolvers.current.nameUpdate?.reject(error)
    }
  })

  const handleEditConfirm = async (
    sponsor: Sponsor,
    name: string,
    newImage?: File,
    imageRemoved?: boolean
  ) => {
    // If no changes, close dialog immediately
    if (!newImage && name === sponsor.name && !imageRemoved) {
      setState(prev => ({
        ...prev,
        showEditDialog: false,
        editingSponsorId: null
      }))
      return
    }

    try {
      // Run operations SEQUENTIALLY to avoid server race conditions

      // 1. Update name first (if changed)
      if (name !== sponsor.name) {
        await new Promise<void>((resolve, reject) => {
          editOperationResolvers.current.nameUpdate = {resolve, reject}
          updateSponsorName(sponsor.id, name)
        })
      }

      // 2. Then handle image changes
      if (imageRemoved) {
        await new Promise<void>((resolve, reject) => {
          editOperationResolvers.current.imageDelete = {resolve, reject}
          deleteSponsorImage(sponsor.id)
        })
      } else if (newImage) {
        await new Promise<void>((resolve, reject) => {
          editOperationResolvers.current.imageUpdate = {resolve, reject}
          updateSponsorImage(sponsor.id, newImage)
        })
      }

      // All operations succeeded, refetch and close dialog
      refetch()
      setState(prev => ({
        ...prev,
        showEditDialog: false,
        editingSponsorId: null
      }))
    } catch (error) {
      // Error is already handled by the hook's onError callback (handleOperationError)
    } finally {
      editOperationResolvers.current = {}
    }
  }

  const handleEditCancel = () => {
    setState(prev => ({
      ...prev,
      showEditDialog: false,
      editingSponsorId: null
    }))
    editOperationResolvers.current = {}
  }

  const handleDeleteSponsor = (sponsor: Sponsor) => {
    setState(prev => ({
      ...prev,
      selectedSponsor: sponsor,
      showDeleteDialog: true,
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  const handleDeleteConfirm = (sponsor: Sponsor) => {
    deleteSponsor(sponsor.id)
  }

  const handleDeleteCancel = () => {
    setState(prev => ({
      ...prev,
      showDeleteDialog: false,
      selectedSponsor: null
    }))
  }

  const handleAssignSponsor = (sponsor: Sponsor) => {
    setState(prev => ({
      ...prev,
      assigningSponsorId: sponsor.id,
      showAssignDialog: true,
      assignDialogType: sponsor.type,
      fetchError: null,
      operationError: null,
      directOperationError: null
    }))
  }

  const handleAssignCancel = () => {
    setState(prev => ({
      ...prev,
      showAssignDialog: false,
      assigningSponsorId: null,
      assignDialogType: null
    }))
  }

  const handleCommunitySelected = () => {
    // Close the dialog after selection
    setState(prev => ({
      ...prev,
      showAssignDialog: false,
      assigningSponsorId: null,
      assignDialogType: null
    }))
  }

  const handleModulesSelected = () => {
    // Close the dialog after selection
    setState(prev => ({
      ...prev,
      showAssignDialog: false,
      assigningSponsorId: null,
      assignDialogType: null
    }))
  }

  const handleImageUpload = async (sponsor: Sponsor, image: File) => {
    try {
      await new Promise<void>((resolve, reject) => {
        directImageOperationResolver.current = {resolve, reject}
        updateSponsorImage(sponsor.id, image)
      })
      refetch()
    } catch (error) {
      // Error is already handled by the hook's onError callback (handleOperationError)
    } finally {
      directImageOperationResolver.current = null
    }
  }

  const handleImageDelete = async (sponsor: Sponsor) => {
    try {
      await new Promise<void>((resolve, reject) => {
        directImageOperationResolver.current = {resolve, reject}
        deleteSponsorImage(sponsor.id)
      })
      refetch()
    } catch (error) {
      // Error is already handled by the hook's onError callback (handleOperationError)
    } finally {
      directImageOperationResolver.current = null
    }
  }

  return (
    <Layout
      title={
        <TitleText>
          <FormattedMessage id="admin.manageSponsors.sponsor_manager" />
        </TitleText>
      }
      toast={
        <SponsorManagerToast
          error={state.fetchError || state.directOperationError}
        />
      }
    >
      <div className="mx-auto mb-2 w-full space-y-8 px-2 md:mb-4 md:px-4 lg:mb-6 lg:w-2/3 lg:px-6 xl:mb-8 xl:max-w-7xl xl:px-8">
        <Breadcrumb>
          <BreadcrumbItem
            path={TOOL_SELECTOR_ROUTE}
            to={TOOL_SELECTOR_ROUTE}
            label={<FormattedMessage id="admin.toolSelector.title" />}
          />
          <BreadcrumbItem
            path={SPONSOR_MANAGER_ROUTE}
            to={SPONSOR_MANAGER_ROUTE}
            label={
              <FormattedMessage id="admin.manageSponsors.sponsor_manager" />
            }
          />
        </Breadcrumb>

        {getSponsorsBusy ||
        creatingBusy ||
        deletingBusy ||
        imageUpdateBusy ||
        nameUpdateBusy ||
        deletingSponsorImageBusy ? (
          <LoadingIndicator />
        ) : (
          <>
            <SponsorSection
              title={
                <FormattedMessage id="admin.manageSponsors.national_sponsors" />
              }
              testId="national"
              sponsors={nationalSponsors}
              onAddSponsor={handleAddNationalSponsor}
              onEditSponsor={handleEditSponsor}
              onDeleteSponsor={handleDeleteSponsor}
              onAssignSponsor={handleAssignSponsor}
              onImageUpload={handleImageUpload}
              onImageDelete={handleImageDelete}
            />

            <SponsorSection
              title={
                <FormattedMessage id="admin.manageSponsors.community_sponsors" />
              }
              testId="community"
              sponsors={communitySponsors}
              onAddSponsor={handleAddCommunitySponsor}
              onEditSponsor={handleEditSponsor}
              onDeleteSponsor={handleDeleteSponsor}
              onAssignSponsor={handleAssignSponsor}
              onImageUpload={handleImageUpload}
              onImageDelete={handleImageDelete}
            />
          </>
        )}
      </div>

      <DeleteSponsorDialog
        sponsor={state.selectedSponsor}
        open={state.showDeleteDialog}
        error={state.operationError}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <AddSponsorDialog
        sponsorType={state.addingSponsorType}
        open={state.showAddDialog}
        onClose={handleAddCancel}
        onConfirm={handleAddConfirm}
        error={state.operationError}
        isBusy={creatingBusy}
      />

      {currentEditingSponsor && (
        <EditSponsorDialog
          sponsor={currentEditingSponsor}
          open={state.showEditDialog}
          error={state.operationError}
          onClose={handleEditCancel}
          onConfirm={handleEditConfirm}
          isBusy={imageUpdateBusy || nameUpdateBusy || deletingSponsorImageBusy}
        />
      )}

      {state.assignDialogType === 'COMMUNITY' && (
        <AssignCommunitySponsorDialog
          sponsor={currentAssigningSponsor}
          sponsors={[...(sponsors || [])]}
          open={state.showAssignDialog}
          onClose={handleAssignCancel}
          onCommunitySelected={handleCommunitySelected}
          onImageUpload={handleImageUpload}
          onImageDelete={handleImageDelete}
        />
      )}

      {state.assignDialogType === 'NATIONAL' && (
        <AssignNationalSponsorDialog
          sponsor={currentAssigningSponsor}
          sponsors={[...(sponsors || [])]}
          open={state.showAssignDialog}
          onClose={handleAssignCancel}
          onModulesSelected={handleModulesSelected}
          onImageUpload={handleImageUpload}
          onImageDelete={handleImageDelete}
        />
      )}
    </Layout>
  )
}

type ToastProps = Readonly<{error: Error | null}>

function SponsorManagerToast({error}: ToastProps) {
  return (
    <>
      {error && (
        <Toast variant="error">
          <FormattedMessage id="general.GenericError" />
        </Toast>
      )}
    </>
  )
}

export default SponsorManagerPage
